import { useEffect, useState, useRef, useCallback } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowUpRight,
  ArrowRight,
  CheckCircle2,
  Award,
  DollarSign,
  Loader2,
  Sparkles,
  Bookmark,
  BookmarkCheck,
  Clock,
  Briefcase,
  Layers,
  TrendingUp,
  Wrench,
  GraduationCap,
  BookOpen,
  Users,
  Rss,
  Building,
  MessageCircle,
  ChevronDown,
  Star,
  Sun,
  Play,
  Handshake,
  Newspaper,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useApiFetch } from "@/hooks/useApiFetch";
import { LoadingState, RetryState } from "@/components/LoadStates";
import ChartCover from "@/components/ChartCover";
import { track } from "@/lib/analytics";

function trackAffiliateClick(url, resource_name) {
  try {
    // Slug is always the last URL segment on /roadmaps/:slug
    const parts = window.location.pathname.split("/").filter(Boolean);
    const hub_slug = parts[parts.length - 1];
    let url_host = "";
    try {
      url_host = new URL(url).hostname.replace(/^www\./, "");
    } catch (_) {}
    // Human-friendly partner label from host
    const partner =
      /wallstreetoasis/i.test(url_host) ? "Wall Street Oasis"
      : /tradingview/i.test(url_host) ? "TradingView"
      : /coursera/i.test(url_host) ? "Coursera"
      : /udemy/i.test(url_host) ? "Udemy"
      : /cfa/i.test(url_host) ? "CFA Institute"
      : url_host || "unknown";
    track("affiliate_click", { hub_slug, partner, resource_name, url_host });
  } catch (_) {}
}

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

export default function RoadmapDetail() {
  const { slug } = useParams();
  const [notFound, setNotFound] = useState(false);
  const { user, openAuth, refresh } = useAuth();
  const learningRef = useRef(null);

  // Fetch the specific hub (auto-retries once on transient failure)
  const hubFetcher = useCallback(async () => {
    try {
      const res = await api.get(`/roadmaps/${slug}`);
      return res.data;
    } catch (err) {
      if (err?.response?.status === 404) {
        setNotFound(true);
        return null; // treat as loaded-but-missing
      }
      throw err;
    }
  }, [slug]);
  const { data, loading, error, retry } = useApiFetch(hubFetcher, { deps: [slug] });

  // Related hubs list — fetched independently so a delayed / failed list
  // never prevents the main hub from rendering.
  const listFetcher = useCallback(async () => (await api.get("/roadmaps")).data, []);
  const { data: allHubsData } = useApiFetch(listFetcher, { deps: [] });
  const allHubs = allHubsData || [];

  useEffect(() => {
    setNotFound(false);
  }, [slug]);

  // Record a hub_view once we have the loaded hub. Fire-and-forget.
  useEffect(() => {
    if (data?.slug) {
      track("hub_view", { hub_slug: data.slug, hub_title: data.title });
    }
  }, [data?.slug, data?.title]);

  const bookmarked = user?.bookmarked_hubs?.includes(slug);

  const toggleBookmark = async () => {
    if (!user) {
      openAuth({
        tab: "signup",
        context: {
          title: "Create a free account to bookmark",
          subtitle: "Save your favourite Career Hubs to your dashboard.",
          onSuccess: async () => {
            try {
              await api.post("/user/bookmarks/hubs", { slug });
              await refresh();
              toast.success("Bookmarked to your dashboard.");
            } catch (_) {
              toast.error("Please try again.");
            }
          },
        },
      });
      return;
    }
    try {
      if (bookmarked) {
        await api.delete(`/user/bookmarks/hubs/${slug}`);
        toast.success("Removed from bookmarks.");
      } else {
        await api.post("/user/bookmarks/hubs", { slug });
        toast.success("Bookmarked to your dashboard.");
      }
      await refresh();
    } catch (_) {
      toast.error("Please try again.");
    }
  };

  const scrollToLearning = () => {
    learningRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <LoadingState label="Loading Career Hub…" />
      </div>
    );
  }
  if (error && !notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link to="/roadmaps" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#0A2540] mb-6">
          <ArrowLeft className="w-4 h-4" /> All Career Hubs
        </Link>
        <RetryState
          title="Couldn't load this Career Hub."
          body="The server may be briefly restarting. Please try again in a moment."
          onRetry={retry}
          testId="hub-retry-state"
        />
      </div>
    );
  }
  if (notFound || !data) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <h1 className="font-display text-4xl text-[#0A2540] font-medium">Career Hub not found</h1>
        <Link to="/roadmaps" className="mt-6 inline-flex items-center gap-2 text-[#0A2540] underline">
          <ArrowLeft className="w-4 h-4" /> Back to Career Hubs
        </Link>
      </div>
    );
  }

  if (data.status === "coming_soon") {
    // No dedicated Coming Soon detail page — send users back to the listing
    return <Navigate to="/roadmaps" replace />;
  }

  const o = data.overview_sections || {};
  const qf = data.quick_facts || {};
  const related = (data.related || []).map((s) => allHubs.find((h) => h.slug === s)).filter(Boolean);

  // The Premium Playbook block, extracted so it can be positioned differently
  // per hub (these hubs show it right after Skills; others keep the original spot).
  const PLAYBOOK_AFTER_SKILLS = new Set(["investment-banking", "private-equity", "venture-capital", "energy", "hedge-funds", "real-estate"]);
  const playbookSection = data.playbook ? (() => {
    const LIVE_PLAYBOOK_ANCHORS_INNER = {
      "investment-banking": "investment-banking-playbook",
      "private-equity": "private-equity-playbook",
      "energy": "energy-finance-playbook",
    };
    const anchor = LIVE_PLAYBOOK_ANCHORS_INNER[data.slug];
    const isComingSoon = !!data.playbook.coming_soon || !anchor;
    return (
      <>
        <SectionHeading eyebrow="Premium Playbook" title={data.playbook.section_heading || "The definitive playbook for this career."} accent />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-[#C6A15B]/30 bg-[#FBF7EE] vestryn-accent-ring p-8 sm:p-12">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold vestryn-accent">
              <Star className="w-3.5 h-3.5" />
              Premium · Playbook
            </div>
            <h3 className="font-display text-2xl sm:text-3xl text-[#0A2540] mt-3 leading-tight">
              {data.playbook.title}
            </h3>
            <p className="mt-4 text-slate-700 leading-relaxed whitespace-pre-line">
              {data.playbook.tagline}
            </p>
            {data.playbook.includes_label && (
              <div className="mt-6 text-[11px] uppercase tracking-[0.22em] font-semibold vestryn-accent">
                {data.playbook.includes_label}
              </div>
            )}
            {data.playbook.features?.length > 0 && (
              <ul className={`${data.playbook.includes_label ? "mt-3" : "mt-6"} grid sm:grid-cols-2 gap-x-6 gap-y-2.5 text-sm text-slate-700`}>
                {data.playbook.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <span
                      aria-hidden="true"
                      className="mt-[8px] h-[3px] w-[3px] shrink-0 rotate-45 bg-[#C6A15B]/80"
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            )}
            {data.playbook.note && (
              <p className="mt-5 text-sm text-slate-500 leading-relaxed italic">
                {data.playbook.note}
              </p>
            )}
            <div className="mt-8">
              {isComingSoon ? (
                <span
                  data-testid="hub-playbook-coming-soon"
                  className="inline-flex items-center gap-2 rounded-full border border-[#C6A15B]/40 bg-white/60 px-4 py-2 text-[11px] uppercase tracking-[0.22em] font-semibold vestryn-accent"
                >
                  Coming Soon
                </span>
              ) : (
                <Link
                  to={`/playbooks#${anchor}`}
                  data-testid="hub-playbook-cta"
                  className="inline-flex items-center gap-2 bg-[#0A2540] text-white rounded-full px-5 py-3 text-sm font-semibold hover:bg-[#051A2E] transition-colors"
                >
                  View Playbook
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          </div>
        </div>
      </>
    );
  })() : null;

  return (
    <div className="vestryn-soft-gradient">
      {/* HERO */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-10">
        <Link to="/roadmaps" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#0A2540]" data-testid="back-to-roadmaps">
          <ArrowLeft className="w-4 h-4" /> All Career Hubs
        </Link>

        <motion.div
          {...fadeUp}
          transition={{ duration: 0.5 }}
          className="mt-5 relative rounded-[2rem] overflow-hidden border border-slate-100 shadow-[0_20px_60px_rgba(10,37,64,0.08)] h-[260px] sm:h-[340px]"
        >
          <ChartCover variant={data.chart || "line"} label={data.title} slug={data.slug} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540] via-[#0A2540]/40 to-transparent" />
          <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-10">
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/70 font-semibold flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 vestryn-accent" />
              Career Hub
            </div>
            <h1 className="font-display text-4xl sm:text-6xl tracking-tight text-white font-medium mt-2" data-testid="roadmap-title">
              {data.title}
            </h1>
          </div>
        </motion.div>

        <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.05 }} className="mt-6 flex flex-wrap items-start gap-4 justify-between">
          <p className="text-lg text-slate-600 leading-relaxed max-w-3xl" data-testid="hub-hero-subtitle">
            {data.hero_subtitle}
          </p>
          <button
            type="button"
            onClick={toggleBookmark}
            data-testid="hub-bookmark-btn"
            className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium border transition-colors ${
              bookmarked
                ? "bg-[#C6A15B]/10 text-[#0A2540] border-[#C6A15B]/40"
                : "bg-white text-slate-700 border-slate-200 hover:border-[#0A2540]/25 hover:bg-slate-50"
            }`}
          >
            {bookmarked ? <BookmarkCheck className="w-3.5 h-3.5 vestryn-accent" /> : <Bookmark className="w-3.5 h-3.5" />}
            {bookmarked ? "Bookmarked" : "Bookmark this Hub"}
          </button>
        </motion.div>

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={scrollToLearning}
            data-testid="hub-explore-learning"
            className="group inline-flex items-center gap-2 bg-[#0A2540] text-white rounded-full pl-5 pr-4 py-3 text-sm font-semibold hover:bg-[#051A2E] transition-all duration-300 active:scale-95 shadow-[0_10px_30px_rgba(10,37,64,0.25)] hover:shadow-[0_16px_40px_rgba(198,161,91,0.28),_0_10px_30px_rgba(10,37,64,0.25)] hover:-translate-y-0.5"
          >
            Explore Learning Resources
            <ArrowRight className="w-4 h-4" />
          </button>
          {(() => {
            const LIVE_PLAYBOOK_ANCHORS = {
              "investment-banking": "investment-banking-playbook",
              "private-equity": "private-equity-playbook",
              "energy": "energy-finance-playbook",
            };
            const anchor = LIVE_PLAYBOOK_ANCHORS[data.slug];
            const btnClass =
              "inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium border border-[#C6A15B]/40 text-[#0A2540] bg-white hover:bg-[#C6A15B]/5 transition-colors";
            if (anchor) {
              return (
                <Link
                  to={`/playbooks#${anchor}`}
                  data-testid="hub-view-playbook"
                  className={btnClass}
                >
                  <Star className="w-4 h-4 vestryn-accent" />
                  View Premium Playbook
                </Link>
              );
            }
            return (
              <button
                type="button"
                data-testid="hub-view-playbook"
                onClick={() => toast.message("Premium Playbook launching soon.")}
                className={btnClass}
              >
                <Star className="w-4 h-4 vestryn-accent" />
                Playbook coming soon
              </button>
            );
          })()}
        </div>

        <p className="mt-8 text-xs sm:text-sm italic text-slate-500 leading-relaxed max-w-3xl" data-testid="hub-context-note">
          Context note: Career information, compensation, recruiting timelines, and market practices can vary by country, firm, role, and year. Use this hub as educational guidance and verify current local information before making important career decisions.
        </p>

        <p className="mt-2 text-xs sm:text-sm text-slate-500 leading-relaxed max-w-3xl" data-testid="hub-free-guide-cta">
          Build your foundation first. Explore the free{" "}
          <a
            href="https://payhip.com/b/Eyemc"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#0A2540] underline underline-offset-2 hover:text-[#051A2E] transition-colors"
            data-testid="hub-free-guide-link"
          >
            Finance Interview Foundations Guide
          </a>{" "}
          →
        </p>
      </section>

      {/* OVERVIEW */}
      <SectionHeading eyebrow="Career Overview" title={`What ${data.title} really is.`} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-4">
          {o.what_it_is && <OverviewCard icon={Briefcase} label="What it is" body={o.what_it_is} />}
          {o.what_you_do && <OverviewCard icon={Layers} label="What you actually do" body={o.what_you_do} />}
          {o.daily_responsibilities?.length > 0 && (
            <BulletCard icon={CheckCircle2} label="Daily responsibilities" items={o.daily_responsibilities} />
          )}
          {o.industries?.length > 0 && (
            <ChipCard icon={Building} label="Industries & sub-sectors" items={o.industries} />
          )}
          {o.work_environment && <OverviewCard icon={Users} label="Work environment" body={o.work_environment} />}
          {o.career_progression?.length > 0 && (
            <BulletCard icon={TrendingUp} label="Career progression" items={o.career_progression} numbered note={o.career_progression_note} />
          )}
          {o.exit_opps?.length > 0 && (
            <ChipCard icon={ArrowUpRight} label="Exit opportunities" items={o.exit_opps} note={o.exit_opps_note} />
          )}
        </div>

        <aside className="lg:col-span-4">
          <div className="rounded-3xl border border-slate-100 bg-white p-6 sticky top-20 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold vestryn-accent">Quick Facts</div>
            <ul className="mt-4 space-y-3 text-sm">
              <QuickFact icon={Briefcase} label="Typical entry role" value={qf.entry_role} />
              <QuickFact icon={Clock} label="Typical working hours" value={qf.typical_hours} />
              <QuickFact icon={Wrench} label="Core skills" value={qf.core_skills} />
              <QuickFact icon={TrendingUp} label="Career progression" value={qf.career_progression} />
              <QuickFact icon={DollarSign} label="Compensation" value={qf.compensation} />
            </ul>
          </div>
        </aside>
      </div>

      {/* DAY IN THE LIFE */}
      {data.day_in_life?.length > 0 && (
        <>
          <SectionHeading eyebrow="Day in the Life" title="What a real day looks like." />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl border border-slate-100 bg-white p-6 sm:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
              <div className="relative">
                <div className="absolute left-4 top-2 bottom-2 w-px bg-slate-200" aria-hidden />
                <ol className="space-y-4">
                  {data.day_in_life.map((row, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -8 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.35, delay: i * 0.04 }}
                      className="relative pl-12"
                      data-testid={`day-in-life-${i}`}
                    >
                      <div className="absolute left-0 top-0 w-8 h-8 rounded-full bg-[#0A2540] text-white grid place-items-center">
                        {i === 0 ? <Sun className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                      </div>
                      <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
                        {row.time}
                      </div>
                      <div className="font-display text-lg text-[#0A2540] font-medium">{row.activity}</div>
                      {row.note && <div className="text-sm text-slate-600 mt-0.5">{row.note}</div>}
                    </motion.li>
                  ))}
                </ol>
              </div>
              {data.day_in_life_note && (
                <p className="mt-5 text-xs text-slate-500 leading-relaxed" data-testid="day-in-life-note">
                  {data.day_in_life_note}
                </p>
              )}
            </div>
          </div>
        </>
      )}

      {/* CAREER ROADMAP */}
      {data.roadmap_stages?.length > 0 && (
        <>
          <SectionHeading eyebrow="Career Roadmap" title={`How to break into ${data.title}.`} />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative">
              <div className="absolute left-4 sm:left-6 top-2 bottom-2 w-px bg-slate-200" aria-hidden />
              <div className="space-y-4">
                {data.roadmap_stages.map((step, i) => (
                  <motion.div
                    key={step.title + i}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.03 }}
                    className="relative pl-14 sm:pl-20"
                    data-testid={`roadmap-stage-${i}`}
                  >
                    <div className="absolute left-0 sm:left-2 top-0 w-9 h-9 rounded-full bg-[#0A2540] text-white grid place-items-center font-display text-sm">
                      {i + 1}
                    </div>
                    <div className="rounded-3xl border border-slate-100 bg-white p-5 sm:p-6 vestryn-card-lift">
                      <h3 className="font-display text-lg sm:text-xl text-[#0A2540] font-medium">{step.title}</h3>
                      <p className="mt-2 text-slate-600 leading-relaxed">{step.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* INDIA CONTEXT (hubs that define india_context) */}
      {data.india_context?.items?.length > 0 && (
        <>
          <SectionHeading eyebrow={data.india_context.eyebrow} title={data.india_context.title} />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="india-context-section">
            <p className="text-slate-600 leading-relaxed max-w-3xl">{data.india_context.intro}</p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {data.india_context.items.map((it, i) => (
                <motion.div
                  key={it.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.03 }}
                  className="rounded-3xl border border-slate-100 bg-white p-5 vestryn-card-lift"
                  data-testid={`india-context-card-${i}`}
                >
                  <h3 className="font-display text-lg text-[#0A2540] font-medium">{it.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{it.description}</p>
                </motion.div>
              ))}
            </div>
            {data.india_context.note && (
              <p className="mt-5 text-xs text-slate-500 leading-relaxed max-w-3xl italic" data-testid="india-context-note">
                {data.india_context.note}
              </p>
            )}
          </div>
        </>
      )}

      {/* SKILLS TO MASTER */}
      {data.skills_detailed?.length > 0 && (
        <>
          <SectionHeading eyebrow="Skills to Master" title="What actually gets you hired." />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.skills_detailed.map((s, i) => (
              <motion.div
                key={s.name}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.03 }}
                className="rounded-3xl border border-slate-100 bg-white p-5 vestryn-card-lift"
                data-testid={`skill-card-${i}`}
              >
                <div className="w-9 h-9 rounded-xl bg-[#0A2540]/5 text-[#0A2540] grid place-items-center">
                  <Wrench className="w-4 h-4" />
                </div>
                <div className="font-display text-lg text-[#0A2540] mt-3">{s.name}</div>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">{s.description}</p>
                {s.why && (
                  <div className="mt-3 text-xs text-slate-500 border-t border-slate-100 pt-3">
                    <span className="uppercase tracking-[0.18em] font-semibold text-slate-400">Why it matters — </span>
                    {s.why}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </>
      )}

      {/* PREMIUM PLAYBOOK (hubs in PLAYBOOK_AFTER_SKILLS: immediately after Skills) */}
      {PLAYBOOK_AFTER_SKILLS.has(data.slug) && playbookSection}

      {/* RECOMMENDED LEARNING RESOURCES */}
      <div ref={learningRef} id="learning-resources" style={{ scrollMarginTop: "80px" }}>
        {data.learning_resources ? (
          <LearningResourcesSection resources={data.learning_resources} />
        ) : (
          <>
            <SectionHeading eyebrow="Certifications & Learning" title="Recommended Certifications & Learning." />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <LearningGroup icon={Award} title="Recommended Certifications" items={data.certifications_recs} />
              <LearningGroup icon={GraduationCap} title="Recommended Courses" items={data.courses_recs} />
              <LearningGroup icon={Play} title="Learning Platforms" items={data.learning_platforms} />
              <LearningGroup icon={Building} title="Professional Organizations" items={data.professional_orgs} />
              <LearningGroup icon={Users} title="Communities" items={data.communities} />
              <LearningGroup icon={BookOpen} title="Books" items={data.books} />
              <LearningGroup icon={Rss} title="Newsletters" items={data.newsletters} />
            </div>
            <p className="max-w-6xl mx-auto mt-4 px-4 sm:px-6 lg:px-8 text-[11px] text-slate-400">
              VantStride recommends these platforms as a starting point. Affiliate and partner links will be added over time — see our{" "}
              <Link to="/affiliate-disclosure" className="underline hover:text-[#0A2540]">Affiliate Disclosure</Link>.
            </p>
          </>
        )}
      </div>

      {/* CAREER AI */}
      <SectionHeading eyebrow="Career AI" title={`Ask VantStride AI about ${data.title}.`} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-slate-100 bg-white p-8 sm:p-10 flex flex-col md:flex-row gap-6 items-start md:items-center vestryn-card-lift">
          <div className="w-12 h-12 rounded-2xl bg-[#0A2540] text-white grid place-items-center flex-shrink-0">
            <MessageCircle className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <p className="text-slate-700 leading-relaxed">
              VantStride AI is a finance-focused assistant that can help with career questions, technical interviews, valuation, financial modeling, resume review, networking, and learning paths — including topics specific to {data.title}.
            </p>
            {data.ai_prompts?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="hub-ai-prompts">
                {data.ai_prompts.map((p) => (
                  <Link
                    key={p}
                    to={`/career-ai?prompt=${encodeURIComponent(p)}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 hover:bg-[#0A2540] hover:text-white transition-colors"
                  >
                    {p}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <Link
            to="/career-ai"
            data-testid="hub-cta-career-ai"
            className="inline-flex items-center gap-2 bg-white text-[#0A2540] rounded-full px-5 py-3 text-sm font-semibold border border-slate-200 hover:border-[#0A2540]/25 hover:bg-slate-50 transition-colors whitespace-nowrap"
          >
            Chat with VantStride AI
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* TOP EMPLOYERS */}
      {data.top_employers?.length > 0 && (
        <>
          <SectionHeading eyebrow="Top Employers" title={`Where ${data.title} careers get built.`} />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6" data-testid="hub-top-employers">
            {data.top_employers.map((group) => (
              <div key={group.category}>
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold vestryn-accent mb-3">
                  {group.category}
                </div>
                <div className="flex flex-wrap gap-2">
                  {group.firms.map((firm) => (
                    <span
                      key={firm}
                      className="text-sm px-3.5 py-2 rounded-full bg-white border border-slate-100 text-[#0A2540]"
                    >
                      {firm}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* INTERVIEW QUESTIONS */}
      {data.interview_questions?.length > 0 && (
        <>
          <SectionHeading eyebrow="Interview Prep" title="Commonly asked interview questions." />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="hub-interview-questions">
            <ol className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.interview_questions.map((q, i) => (
                <li key={q} className="flex gap-3 rounded-2xl border border-slate-100 bg-white p-4">
                  <span className="text-[11px] font-semibold text-slate-400 min-w-[24px]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm text-slate-700 leading-relaxed">{q}</span>
                </li>
              ))}
            </ol>
          </div>
        </>
      )}

      {/* FEATURED PREMIUM PLAYBOOK (hubs not in PLAYBOOK_AFTER_SKILLS keep it here) */}
      {!PLAYBOOK_AFTER_SKILLS.has(data.slug) && playbookSection}

      {/* FAQ */}
      {data.faqs?.length > 0 && (
        <>
          <SectionHeading eyebrow="FAQ" title="Frequently Asked Questions." />
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-3">
            {data.faqs.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} idx={i} />
            ))}
          </div>
        </>
      )}

      {/* RELATED HUBS */}
      {related.length > 0 && (
        <>
          <SectionHeading eyebrow="Related Career Hubs" title="Students interested in this may also explore." />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pb-14 sm:pb-16">
            {related.map((h) => (
              <Link
                key={h.slug}
                to={`/roadmaps/${h.slug}`}
                data-testid={`related-hub-${h.slug}`}
                className="group rounded-3xl border border-slate-100 bg-white overflow-hidden vestryn-card-lift"
              >
                <div className="h-24 relative">
                  <ChartCover variant={h.chart || "line"} slug={h.slug} />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/80 to-transparent" />
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-slate-500">
                    Career Hub
                  </div>
                  <div className="font-display text-lg text-[#0A2540] mt-1">{h.title}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#0A2540]">
                    Open <ArrowUpRight className="w-3 h-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- helpers ---------- */

function SectionHeading({ eyebrow, title, accent }) {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-14 sm:mt-16 mb-6">
      <div className={`text-xs uppercase tracking-[0.22em] font-semibold ${accent ? "vestryn-accent" : "text-slate-500"}`}>
        {eyebrow}
      </div>
      <h2 className="font-display mt-2 text-2xl sm:text-3xl tracking-tight text-[#0A2540] font-medium">
        {title}
      </h2>
    </div>
  );
}

function OverviewCard({ icon: Icon, label, body }) {
  return (
    <motion.div
      {...fadeUp}
      transition={{ duration: 0.4 }}
      className="rounded-3xl border border-slate-100 bg-white p-6"
    >
      <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500 flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 text-[#0A2540]" /> {label}
      </div>
      <p className="mt-2 text-slate-700 leading-relaxed">{body}</p>
    </motion.div>
  );
}

function BulletCard({ icon: Icon, label, items, numbered, note }) {
  return (
    <motion.div
      {...fadeUp}
      transition={{ duration: 0.4 }}
      className="rounded-3xl border border-slate-100 bg-white p-6"
    >
      <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500 flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 text-[#0A2540]" /> {label}
      </div>
      <ul className="mt-3 space-y-2 text-sm text-slate-700">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2">
            {numbered ? (
              <span className="w-5 h-5 rounded-full bg-[#0A2540]/5 text-[#0A2540] grid place-items-center text-[11px] font-semibold flex-shrink-0">
                {i + 1}
              </span>
            ) : (
              <CheckCircle2 className="w-4 h-4 text-[#0A2540] mt-0.5 flex-shrink-0" />
            )}
            <span>{it}</span>
          </li>
        ))}
      </ul>
      {note && <p className="mt-3 text-xs text-slate-500 leading-relaxed">{note}</p>}
    </motion.div>
  );
}

function ChipCard({ icon: Icon, label, items, note }) {
  return (
    <motion.div
      {...fadeUp}
      transition={{ duration: 0.4 }}
      className="rounded-3xl border border-slate-100 bg-white p-6"
    >
      <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-slate-500 flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 text-[#0A2540]" /> {label}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {items.map((it) => (
          <span key={it} className="text-xs px-2.5 py-1.5 rounded-full bg-slate-100 text-slate-700">
            {it}
          </span>
        ))}
      </div>
      {note && <p className="mt-3 text-xs text-slate-500 leading-relaxed">{note}</p>}
    </motion.div>
  );
}

function QuickFact({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <li className="flex gap-3">
      <div className="w-7 h-7 rounded-lg bg-slate-100 text-[#0A2540] grid place-items-center flex-shrink-0">
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">{label}</div>
        <div className="text-sm text-[#0A2540] mt-0.5">{value}</div>
      </div>
    </li>
  );
}

function LearningGroup({ icon: Icon, title, items }) {
  if (!items?.length) return null;
  return (
    <motion.div
      {...fadeUp}
      transition={{ duration: 0.4 }}
      className="rounded-3xl border border-slate-100 bg-white p-6 vestryn-card-lift"
      data-testid={`learning-group-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-[#0A2540] text-white grid place-items-center">
          <Icon className="w-4 h-4" />
        </div>
        <div className="font-display text-lg text-[#0A2540]">{title}</div>
      </div>
      <ul className="mt-4 space-y-2.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C6A15B] mt-2 flex-shrink-0" />
            <div>
              <div className="text-sm font-medium text-[#0A2540]">{it.name}</div>
              {it.description && <div className="text-xs text-slate-500 mt-0.5">{it.description}</div>}
            </div>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="rounded-3xl border border-slate-100 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`faq-toggle-${idx}`}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="font-medium text-[#0A2540]">{q}</span>
        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed border-t border-slate-100">
          <p className="pt-3">{a}</p>
        </div>
      )}
    </div>
  );
}


/* ---------- Recommended Learning Resources (premium redesign) ---------- */

const SECTION_ICONS = {
  partners: Handshake,
  books: BookOpen,
  newsletters: Newspaper,
  communities: Users,
};

function LearningResourcesSection({ resources }) {
  const sections = resources?.sections || [];
  return (
    <div data-testid="learning-resources-section">
      <SectionHeading eyebrow="Recommended Learning Resources" title={resources?.heading_title || "A curated shortlist we actually recommend."} />
      {resources?.intro && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-2 mb-8">
          <p className="text-slate-600 leading-relaxed max-w-3xl">{resources.intro}</p>
        </div>
      )}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14 sm:space-y-20">
        {sections.map((section, idx) => (
          <LearningSubsection key={section.key || idx} section={section} />
        ))}
      </div>
    </div>
  );
}

function LearningSubsection({ section }) {
  const Icon = SECTION_ICONS[section.key] || Award;
  const items = section.items || [];
  const featuredFirst = section.layout === "featured_first" && items.length > 0;

  return (
    <motion.div
      {...fadeUp}
      transition={{ duration: 0.45 }}
      data-testid={`learning-subsection-${section.key || section.eyebrow}`}
    >
      <div className="flex items-start gap-4 mb-6">
        <div className="w-10 h-10 rounded-2xl bg-[#0A2540] text-white grid place-items-center flex-shrink-0">
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold vestryn-accent">
            {section.eyebrow}
          </div>
          <h3 className="font-display text-2xl sm:text-[1.75rem] tracking-tight text-[#0A2540] font-medium mt-1">
            {section.title}
          </h3>
          {section.description && (
            <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-2xl">
              {section.description}
            </p>
          )}
        </div>
      </div>

      {featuredFirst ? (
        <div className="space-y-5">
          <ResourceCard item={items[0]} section={section} idx={0} variant="featured" />
          {items.length > 1 && (
            <div className="grid gap-5 grid-cols-1 md:grid-cols-2">
              {items.slice(1).map((item, i) => (
                <div
                  key={item.name + (i + 1)}
                  className={item.featured_courses?.length ? "md:col-span-2" : ""}
                >
                  <ResourceCard item={item} section={section} idx={i + 1} />
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div
          className={`grid gap-5 grid-cols-1 sm:grid-cols-2 ${
            items.length >= 3 ? "lg:grid-cols-3" : ""
          } ${items.length >= 4 ? "xl:grid-cols-4" : ""}`}
        >
          {items.map((item, i) => (
            <ResourceCard key={item.name + i} item={item} section={section} idx={i} />
          ))}
        </div>
      )}

      {section.note && (
        <p className="mt-6 text-[11px] text-slate-400 leading-relaxed max-w-3xl">
          {section.note}
        </p>
      )}
    </motion.div>
  );
}

function ResourceCard({ item, section, idx, variant }) {
  const courses = item.featured_courses || [];
  const isFeatured = variant === "featured";
  const cta = item.cta || null;
  const ctaActive = cta && cta.url;
  const ctaPlaceholder = cta && !cta.url && cta.placeholder;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: idx * 0.03 }}
      className={`group rounded-2xl border border-slate-100 bg-white shadow-sm hover:shadow-md hover:-translate-y-1 hover:border-[#C6A15B]/40 transition-all duration-300 flex flex-col ${
        isFeatured ? "p-6 sm:p-8" : "p-5 sm:p-6"
      }`}
      data-testid={`resource-card-${(item.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="flex items-start gap-4">
        {item.logo_initials && <BrandMark initials={item.logo_initials} large={isFeatured} />}
        <div className="flex-1 min-w-0">
          {item.badge && (
            <span className="inline-flex items-center rounded-full border border-[#C6A15B]/30 bg-[#C6A15B]/10 text-[#7a5a24] text-[10px] font-semibold tracking-[0.16em] uppercase px-2.5 py-1">
              {item.badge}
            </span>
          )}
          <h4
            className={`font-display text-[#0A2540] font-medium leading-snug ${
              isFeatured ? "text-2xl sm:text-3xl mt-2" : "text-lg mt-3"
            }`}
          >
            {item.url ? (
              <a
                href={item.url}
                target="_blank"
                rel="sponsored noopener noreferrer"
                onClick={() => trackAffiliateClick(item.url, item.name)}
                data-testid={`resource-name-${(item.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="hover:underline underline-offset-4 decoration-[#C6A15B]/60"
              >
                {item.name}
              </a>
            ) : (
              item.name
            )}
          </h4>
          {item.author && (
            <div className="mt-1 text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">
              By {item.author}
            </div>
          )}
        </div>
      </div>

      {item.tagline && (
        <p
          className={`text-slate-600 leading-relaxed ${
            isFeatured ? "mt-4 text-[15px] max-w-3xl" : "mt-3 text-sm flex-1"
          }`}
        >
          {item.tagline}
        </p>
      )}

      {/* Single CTA (TradingView, WSO Forums, etc.) */}
      {ctaActive && (
        <a
          href={cta.url}
          target="_blank"
          rel="sponsored noopener noreferrer"
          onClick={() => trackAffiliateClick(cta.url, item.name)}
          data-testid={`resource-cta-${(item.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`mt-5 self-start inline-flex items-center gap-2 rounded-full pl-5 pr-4 py-2.5 text-xs font-semibold bg-[#0A2540] text-white hover:bg-[#051A2E] hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(10,37,64,0.2)] transition-all`}
        >
          <span>{cta.label}</span>
          <ArrowUpRight className="w-3.5 h-3.5" />
        </a>
      )}
      {ctaPlaceholder && (
        <div
          data-testid={`resource-cta-placeholder-${(item.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          aria-disabled
          className="mt-5 self-start inline-flex items-center gap-2 rounded-full pl-5 pr-4 py-2.5 text-xs font-semibold border border-dashed border-[#0A2540]/25 text-[#0A2540]/55 bg-white cursor-default"
        >
          <span>{cta.label}</span>
          <ArrowUpRight className="w-3.5 h-3.5 opacity-60" />
        </div>
      )}

      {/* Featured Courses grid (WSO) */}
      {courses.length > 0 && (
        <div className="mt-7 pt-7 border-t border-slate-100" data-testid="featured-courses-block">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold vestryn-accent">
            Featured Courses
          </div>
          <div className="mt-4 grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
            {courses.map((c, i) => (
              <FeaturedCourseCard key={c.name + i} course={c} idx={i} />
            ))}
          </div>
        </div>
      )}

      {/* Optional "explore more" text link (Udemy, etc.) */}
      {item.more_link?.url && (
        <p className="mt-4 text-xs text-slate-500" data-testid={`resource-more-${(item.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          {item.more_link.prefix}
          <a
            href={item.more_link.url}
            target="_blank"
            rel="sponsored noopener noreferrer"
            onClick={() => trackAffiliateClick(item.more_link.url, item.name)}
            className="font-medium text-[#0A2540] hover:underline underline-offset-2"
          >
            {item.more_link.label} →
          </a>
        </p>
      )}
    </motion.div>
  );
}

function FeaturedCourseCard({ course, idx }) {
  const cta = course.cta || null;
  const active = cta && cta.url;
  const placeholder = cta && !cta.url && cta.placeholder;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.35, delay: idx * 0.03 }}
      className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 flex flex-col hover:border-[#C6A15B]/40 hover:bg-white transition-colors"
      data-testid={`featured-course-${idx}`}
    >
      <h5 className="font-display text-[15px] text-[#0A2540] font-medium leading-snug">
        {course.url ? (
          <a
            href={course.url}
            target="_blank"
            rel="sponsored noopener noreferrer"
            onClick={() => trackAffiliateClick(course.url, course.name)}
            className="hover:underline underline-offset-4 decoration-[#C6A15B]/60"
            data-testid={`featured-course-link-${idx}`}
          >
            {course.name}
          </a>
        ) : (
          course.name
        )}
      </h5>
      {course.instructor && (
        <div className="mt-1 text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">
          {course.instructor}
        </div>
      )}
      {course.description && (
        <p className="mt-2 text-xs text-slate-600 leading-relaxed flex-1">
          {course.description}
        </p>
      )}
      {active && (
        <a
          href={cta.url}
          target="_blank"
          rel="sponsored noopener noreferrer"
          onClick={() => trackAffiliateClick(cta.url, course.name)}
          data-testid={`featured-course-cta-${idx}`}
          className="mt-3 self-start inline-flex items-center gap-1.5 rounded-full pl-3.5 pr-3 py-1.5 text-[11px] font-semibold bg-[#0A2540] text-white hover:bg-[#051A2E] transition-colors"
        >
          <span>{cta.label}</span>
          <ArrowUpRight className="w-3 h-3" />
        </a>
      )}
      {placeholder && (
        <div
          aria-disabled
          data-testid={`featured-course-cta-placeholder-${idx}`}
          className="mt-3 self-start inline-flex items-center gap-1.5 rounded-full pl-3.5 pr-3 py-1.5 text-[11px] font-semibold border border-dashed border-[#0A2540]/25 text-[#0A2540]/55 bg-white cursor-default"
        >
          <span>{cta.label}</span>
          <ArrowUpRight className="w-3 h-3 opacity-60" />
        </div>
      )}
    </motion.div>
  );
}

function BrandMark({ initials, large }) {
  return (
    <div
      className={`rounded-xl bg-[#0A2540] text-white grid place-items-center flex-shrink-0 relative overflow-hidden ${
        large ? "w-14 h-14" : "w-11 h-11"
      }`}
      aria-hidden
    >
      <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#C6A15B]" />
      <span
        className={`font-display tracking-wide ${large ? "text-base" : "text-[13px]"}`}
      >
        {initials}
      </span>
    </div>
  );
}

